import { Timestamp } from 'firebase/firestore';

/**
 * Timestampを日付文字列に変換
 */
export function formatDate(timestamp: Timestamp | Date, format: 'short' | 'long' = 'short'): string {
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();

  if (format === 'short') {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Timestampを日時文字列に変換
 */
export function formatDateTime(timestamp: Timestamp | Date): string {
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();

  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 時刻のみを取得（HH:MM形式）
 */
export function formatTime(timestamp: Timestamp | Date): string {
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();

  return date.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 相対時間を取得（例: 3時間前）
 */
export function formatRelativeTime(timestamp: Timestamp | Date): string {
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'たった今';
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}分前`;
  }

  if (diffHours < 24) {
    return `${diffHours}時間前`;
  }

  if (diffDays < 7) {
    return `${diffDays}日前`;
  }

  return formatDate(date, 'short');
}

/**
 * 年齢を計算
 */
export function calculateAge(birthDate: Timestamp | Date): number {
  const birth = birthDate instanceof Date ? birthDate : birthDate.toDate();
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * 年齢を範囲表示（ドニー用）
 * 例: 27歳 → "20代後半"
 */
export function formatAgeRange(age: number): string {
  if (age < 20) return '10代';
  const decade = Math.floor(age / 10) * 10;
  const half = age % 10 < 5 ? '前半' : '後半';
  return `${decade}代${half}`;
}

/**
 * 金額をフォーマット
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  }).format(amount);
}

/**
 * 数値をフォーマット
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('ja-JP').format(num);
}

/**
 * 身長をフォーマット
 */
export function formatHeight(cm: number): string {
  return `${cm}cm`;
}

/**
 * 体重をフォーマット
 */
export function formatWeight(kg: number): string {
  return `${kg}kg`;
}
